import { type CSSProperties, useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import type {
  ConversationDraft,
  LeaveSummaryToolCall,
} from '@lms/shared-types'
import { Icon } from '../primitives/Icon'
import { Modal } from '../primitives/Modal'
import { Skeleton } from '../primitives/Skeleton'
import { useToast } from '../primitives/Toast'
import { draftConversation, sendConversationToSlack } from '../../api/conversations'

interface ConversationDraftModalProps {
  open: boolean
  onClose: () => void
  employeeId: string
  employeeName: string
  patternKinds: string[]
  windowDays?: number
}

interface EditableDraft {
  title: string
  opening: string
  talkingPoints: string[]
  questionsToAsk: string[]
  toneWarning: string | null
  nextStep: string
}

/**
 * Slice A3 — Conversation Drafter modal.
 *
 * Manager triggers via "Draft Conversation" button on the Patterns page.
 * Mutation hits POST `/api/v1/employees/:id/conversation-draft` once on
 * open, populates an editable form, and exposes a Copy button so the
 * manager can paste the draft into Slack / email.
 *
 * "Send to Slack" is intentionally out of scope this turn — see A3
 * follow-up. The disabled-but-visible affordance signals the future
 * surface without overpromising.
 */
export function ConversationDraftModal({
  open,
  onClose,
  employeeId,
  employeeName,
  patternKinds = [],
  windowDays = 90,
}: ConversationDraftModalProps): JSX.Element | null {
  const { notify } = useToast()
  const [edited, setEdited] = useState<EditableDraft | null>(null)
  const [notes, setNotes] = useState('')
  const [toolCalls, setToolCalls] = useState<readonly LeaveSummaryToolCall[]>([])
  const [invocationId, setInvocationId] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () =>
      draftConversation(employeeId, {
        patternKinds,
        notes: notes.trim() || null,
        windowDays,
      }),
    onSuccess: (data) => {
      setEdited({
        title: data.draft.title,
        opening: data.draft.opening,
        talkingPoints: [...data.draft.talkingPoints],
        questionsToAsk: [...data.draft.questionsToAsk],
        toneWarning: data.draft.toneWarning,
        nextStep: data.draft.nextStep,
      })
      setToolCalls(data.toolCalls)
      setInvocationId(data.invocationId)
    },
  })

  const slackMutation = useMutation({
    mutationFn: (draft: EditableDraft) =>
      sendConversationToSlack(employeeId, {
        title: draft.title,
        opening: draft.opening,
        talkingPoints: draft.talkingPoints,
        questionsToAsk: draft.questionsToAsk,
        toneWarning: draft.toneWarning,
        nextStep: draft.nextStep,
        invocationId,
      }),
    onSuccess: (res) => {
      if (res.delivered) {
        notify('Sent to your Slack DM.', 'success')
      } else {
        notify(`Slack send failed: ${res.error ?? 'unknown'}`, 'error')
      }
    },
    onError: () => {
      notify('Could not send to Slack.', 'error')
    },
  })

  // Auto-fire the first draft when the modal opens.
  useEffect(() => {
    if (!open) return
    setEdited(null)
    setToolCalls([])
    mutation.reset()
    mutation.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employeeId, patternKinds, windowDays])

  if (!open) return null

  return (
    <Modal open={open} onClose={onClose} title={`Draft conversation — ${employeeName}`}>
      <div data-testid="conversation-draft-modal">
        {mutation.isPending && edited === null ? <DraftSkeleton /> : null}

        {mutation.isError && edited === null ? (
          <DraftError onRetry={() => mutation.mutate()} />
        ) : null}

        {edited ? (
          <DraftForm
            draft={edited}
            onChange={setEdited}
            onCopy={() => copyDraft(edited, notify)}
            onRetry={() => {
              setEdited(null)
              mutation.mutate()
            }}
            retryPending={mutation.isPending}
            onSendSlack={() => slackMutation.mutate(edited)}
            slackPending={slackMutation.isPending}
          />
        ) : null}

        {toolCalls.length > 0 ? <ProvenanceList toolCalls={toolCalls} /> : null}
      </div>
    </Modal>
  )
}

// ── Skeleton ────────────────────────────────────────────────────────────────

function DraftSkeleton(): JSX.Element {
  return (
    <div
      data-testid="conversation-draft-skeleton"
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <Skeleton height={28} width="60%" radius={6} />
      <Skeleton height={48} width="100%" radius={6} />
      <Skeleton height={20} width="40%" radius={6} />
      <Skeleton height={24} width="90%" radius={6} />
      <Skeleton height={24} width="80%" radius={6} />
      <Skeleton height={24} width="85%" radius={6} />
    </div>
  )
}

// ── Error band ──────────────────────────────────────────────────────────────

interface DraftErrorProps {
  onRetry: () => void
}

function DraftError({ onRetry }: DraftErrorProps): JSX.Element {
  return (
    <div
      role="alert"
      data-testid="conversation-draft-error"
      style={{
        padding: '12px 14px',
        background: 'var(--danger-bg)',
        color: 'var(--danger-fg)',
        border: '1px solid var(--danger-line)',
        borderRadius: 10,
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span>Could not draft a conversation. Try again?</span>
      <button
        type="button"
        onClick={onRetry}
        style={{
          padding: '6px 12px',
          background: 'var(--surface)',
          border: '1px solid var(--danger-line)',
          borderRadius: 6,
          cursor: 'pointer',
          color: 'var(--danger-fg)',
          fontWeight: 600,
        }}
      >
        Retry
      </button>
    </div>
  )
}

// ── Editable form ───────────────────────────────────────────────────────────

interface DraftFormProps {
  draft: EditableDraft
  onChange: (next: EditableDraft) => void
  onCopy: () => void
  onRetry: () => void
  retryPending: boolean
  onSendSlack: () => void
  slackPending: boolean
}

const FIELD_LABEL_STYLE: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-3)',
  letterSpacing: '0.04em',
  marginBottom: 4,
}

const INPUT_STYLE: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 14,
  fontFamily: 'inherit',
}

function DraftForm({
  draft,
  onChange,
  onCopy,
  onRetry,
  retryPending,
  onSendSlack,
  slackPending,
}: DraftFormProps): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Field label="Title">
        <input
          data-testid="draft-title"
          aria-label="Conversation title"
          style={INPUT_STYLE}
          value={draft.title}
          onChange={(e) => onChange({ ...draft, title: e.target.value })}
        />
      </Field>

      <Field label="Opening">
        <textarea
          data-testid="draft-opening"
          aria-label="Conversation opening"
          rows={3}
          style={{ ...INPUT_STYLE, resize: 'vertical' }}
          value={draft.opening}
          onChange={(e) => onChange({ ...draft, opening: e.target.value })}
        />
      </Field>

      <Field label="Talking points">
        <ul
          data-testid="draft-talking-points"
          style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 0, listStyle: 'none' }}
        >
          {draft.talkingPoints.map((point, i) => (
            <li key={i} style={{ display: 'flex', gap: 6 }}>
              <span aria-hidden="true" style={{ color: 'var(--text-3)', minWidth: 14, paddingTop: 6 }}>
                •
              </span>
              <textarea
                data-testid={`draft-talking-point-${i}`}
                aria-label={`Talking point ${i + 1}`}
                rows={2}
                style={{ ...INPUT_STYLE, resize: 'vertical' }}
                value={point}
                onChange={(e) => {
                  const next = [...draft.talkingPoints]
                  next[i] = e.target.value
                  onChange({ ...draft, talkingPoints: next })
                }}
              />
            </li>
          ))}
        </ul>
      </Field>

      {draft.questionsToAsk.length > 0 ? (
        <Field label="Questions to ask">
          <ul
            data-testid="draft-questions-to-ask"
            style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 0, listStyle: 'none' }}
          >
            {draft.questionsToAsk.map((question, i) => (
              <li key={i} style={{ display: 'flex', gap: 6 }}>
                <span aria-hidden="true" style={{ color: 'var(--text-3)', minWidth: 14, paddingTop: 6 }}>
                  ?
                </span>
                <textarea
                  data-testid={`draft-question-${i}`}
                  aria-label={`Question to ask ${i + 1}`}
                  rows={2}
                  style={{ ...INPUT_STYLE, resize: 'vertical' }}
                  value={question}
                  onChange={(e) => {
                    const next = [...draft.questionsToAsk]
                    next[i] = e.target.value
                    onChange({ ...draft, questionsToAsk: next })
                  }}
                />
              </li>
            ))}
          </ul>
        </Field>
      ) : null}

      {draft.toneWarning ? (
        <div
          data-testid="draft-tone-warning"
          role="note"
          style={{
            padding: '8px 12px',
            background: 'var(--warning-bg, var(--surface))',
            border: '1px solid var(--warning-line, var(--border))',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--warning-fg, var(--text-2))',
          }}
        >
          ⚠ {draft.toneWarning}
        </div>
      ) : null}

      <Field label="Next step">
        <textarea
          data-testid="draft-next-step"
          aria-label="Conversation next step"
          rows={2}
          style={{ ...INPUT_STYLE, resize: 'vertical' }}
          value={draft.nextStep}
          onChange={(e) => onChange({ ...draft, nextStep: e.target.value })}
        />
      </Field>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid="draft-retry"
          onClick={onRetry}
          disabled={retryPending}
          style={{
            padding: '8px 14px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            cursor: retryPending ? 'not-allowed' : 'pointer',
            color: 'var(--text-2)',
            fontWeight: 500,
            minHeight: 36,
          }}
        >
          {retryPending ? 'Drafting…' : 'Re-draft'}
        </button>
        <button
          type="button"
          data-testid="draft-send-slack"
          onClick={onSendSlack}
          disabled={slackPending}
          aria-label="Send to your Slack DM"
          title="Sends the draft to your own Slack DM so you can review and forward."
          style={{
            padding: '8px 14px',
            background: 'var(--surface)',
            border: '1px solid var(--warning, var(--border))',
            borderRadius: 6,
            cursor: slackPending ? 'not-allowed' : 'pointer',
            color: 'var(--warning-fg, var(--text-2))',
            fontWeight: 500,
            minHeight: 36,
          }}
        >
          {slackPending ? 'Sending…' : <><Icon name="send" size={14} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: 4 }} />Send to Slack</>}
        </button>
        <button
          type="button"
          data-testid="draft-copy"
          onClick={onCopy}
          style={{
            padding: '8px 14px',
            background: 'var(--steel-blue)',
            color: 'var(--surface)',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
            minHeight: 36,
          }}
        >
          Copy
        </button>
      </div>
    </div>
  )
}

interface FieldProps {
  label: string
  children: React.ReactNode
}

function Field({ label, children }: FieldProps): JSX.Element {
  return (
    <div>
      <div style={FIELD_LABEL_STYLE}>{label}</div>
      {children}
    </div>
  )
}

// ── Provenance ──────────────────────────────────────────────────────────────

interface ProvenanceListProps {
  toolCalls: readonly LeaveSummaryToolCall[]
}

function ProvenanceList({ toolCalls }: ProvenanceListProps): JSX.Element {
  return (
    <details
      data-testid="conversation-draft-provenance"
      style={{
        marginTop: 18,
        padding: '8px 12px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        fontSize: 12,
        color: 'var(--text-3)',
      }}
    >
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
        Provenance ({toolCalls.length} tool call{toolCalls.length === 1 ? '' : 's'})
      </summary>
      <ul style={{ marginTop: 8, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {toolCalls.map((tc, i) => (
          <li key={`${tc.toolId}-${i}`}>
            <div style={{ fontWeight: 600, color: 'var(--text-2)' }}>{tc.toolId}</div>
            <div>{tc.summary}</div>
            {tc.renderedSql ? (
              <pre
                style={{
                  marginTop: 4,
                  padding: 6,
                  background: 'var(--surface)',
                  borderRadius: 4,
                  fontSize: 11,
                  overflow: 'auto',
                  maxWidth: '100%',
                }}
              >
                {tc.renderedSql}
              </pre>
            ) : null}
          </li>
        ))}
      </ul>
    </details>
  )
}

// ── Copy formatter ──────────────────────────────────────────────────────────

function formatDraft(draft: ConversationDraft | EditableDraft): string {
  const points = draft.talkingPoints.map((p) => `- ${p}`).join('\n')
  const questions =
    draft.questionsToAsk.length > 0
      ? '\n\nQuestions to ask:\n' + draft.questionsToAsk.map((q) => `- ${q}`).join('\n')
      : ''
  const toneLine = draft.toneWarning ? `\n\n(Tone: ${draft.toneWarning})` : ''
  return [
    draft.title,
    '',
    draft.opening,
    '',
    points,
    `${questions}`,
    '',
    `Next: ${draft.nextStep}${toneLine}`,
  ].join('\n')
}

async function copyDraft(
  draft: EditableDraft,
  notify: (message: string, variant?: 'success' | 'warning' | 'info' | 'error') => void,
): Promise<void> {
  try {
    await navigator.clipboard.writeText(formatDraft(draft))
    notify('Conversation draft copied to clipboard.', 'success')
  } catch {
    notify('Could not copy to clipboard.', 'error')
  }
}
